import { View, Text, ScreenContainer } from "@src/components";
import { sizes } from "@src/constants/sizes";
import { useRouter } from "expo-router";
import { Button } from "@src/components/Button";
import { useUnistyles, StyleSheet } from "react-native-unistyles";
import { useBiometricAuth } from "@src/hooks/useBiometricAuth";
import BiometricIcon from "@src/components/Biometrics/BiometricIcon";
import { useEffect, useState, useCallback } from "react";
import { FocusedSystemBars } from "@src/components/FocusedSystemBars";
import { BackgroundGradient } from "@src/components/BackgroundGradient";
import { track } from "@visioni/a3s-analytics";
import { BiometricVerifyFailed, BiometricVerifyStarted, BiometricVerifySuccess } from "@src/features/auth/analytics";
import { secureStorage, SecureStorageKeys } from "@src/services/secure-storage";
import { setSessionUnlocked, getPostAuthDestination } from "@src/features/auth/utils";
import { usePin } from "@src/hooks/usePin";
import { useFeatureFlags } from "@src/services/feature-flags/feature-flags";

const BiometricVerifyScreen = () => {
  const router = useRouter();
  const { theme } = useUnistyles();
  const { isAvailable, biometryType, isLoading, authenticate } = useBiometricAuth();
  const [authFailed, setAuthFailed] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const { storedPin } = usePin();
  const hasAppPin = storedPin && storedPin.length > 0 && storedPin !== null;
  const { getBooleanFlag } = useFeatureFlags();

  const postAuthDestination = useCallback(() => getPostAuthDestination(getBooleanFlag), [getBooleanFlag]);

  const handleBiometricAuth = useCallback(async () => {
    if (isLoading) return;

    // If biometrics not available, fall back to PIN or setup
    if (!isAvailable) {
      if (hasAppPin) {
        return router.replace("/auth/insert-pin");
      }
      if (!hasAppPin) {
        return router.replace("/auth/pin");
      }
      return router.replace("/auth/biometrics");
    }

    setIsAuthenticating(true);
    setAuthFailed(false);

    track(BiometricVerifyStarted, {
      biometry_type: biometryType || "unknown",
    });

    const success = await authenticate(`Unlock with ${biometryType}`);

    setIsAuthenticating(false);

    if (success) {
      // Mark session as unlocked (user verified biometrics)
      setSessionUnlocked(true);

      track(BiometricVerifySuccess, {
        biometry_type: biometryType || "unknown",
      });
      router.replace(postAuthDestination());
    } else {
      track(BiometricVerifyFailed, {
        biometry_type: biometryType || "unknown",
      });
      setAuthFailed(true);
    }
  }, [isAvailable, isLoading, authenticate, biometryType, router, hasAppPin, postAuthDestination]);

  // Auto-trigger biometric prompt on mount
  useEffect(() => {
    if (!isLoading) {
      handleBiometricAuth();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- baseline 2026-02-19
  }, [isLoading]); // Only depend on isLoading to trigger once when ready

  const handleUsePIN = () => {
    if (hasAppPin) {
      return router.replace("/auth/insert-pin");
    }
    return router.replace("/auth/pin");
  };

  const title =
    isAuthenticating ? "Authenticating..."
    : authFailed ? "Authentication Failed"
    : `Unlock with ${biometryType || "Biometrics"}`;

  return (
    <>
      <FocusedSystemBars style="light" />
      <BackgroundGradient>
        <ScreenContainer
          edges={["top", "bottom"]}
          style={styles.screenContainer}
          contentMaxWidth={sizes.maxWidthSm}
          scrollable
        >
          <View flex={1} px="4xl" justifyContent="center" alignItems="center">
            {/* Biometric Icon */}
            <BiometricIcon biometryType={biometryType || "Biometric"} />

            {/* Title */}
            <View mt="6xl" alignItems="center">
              <Text textStyle="h3/regular" textAlign="center" style={styles.text}>
                {title}
              </Text>
            </View>

            {/* Error state with retry options */}
            {authFailed && (
              <View mt="8xl" w="100%">
                <Button
                  label="Try Again"
                  textStyle="md/regular"
                  size="md"
                  variant="primary"
                  variantColor="blue"
                  onPress={handleBiometricAuth}
                  disabled={isAuthenticating}
                />
                {hasAppPin && (
                  <View mt="3xl">
                    <Button
                      label="Use Passcode"
                      textStyle="md/regular"
                      size="md"
                      variant="secondary"
                      onPress={handleUsePIN}
                      disabled={isAuthenticating}
                      textColor={theme.colors.white.default}
                      style={styles.borderButton}
                    />
                  </View>
                )}
              </View>
            )}
          </View>
        </ScreenContainer>
      </BackgroundGradient>
    </>
  );
};

const styles = StyleSheet.create((theme) => ({
  screenContainer: {
    backgroundColor: "transparent",
  },
  text: {
    color: theme.colors.interactive.buttons.default_foreground,
  },
  borderButton: {
    borderColor: theme.colors.surface1.foregroundSecondary,
  },
}));

export default BiometricVerifyScreen;
